import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ProjectOutlined
} from "@ant-design/icons";
import { PageHeader } from "@mj/shared-ui";
import {
  Typography,
  Table,
  Button,
  Space,
  Card,
  Tag,
  Select,
  Tooltip
} from "antd";
import dayjs from "dayjs";
import React from "react";
import { toast } from "react-toastify";

import Layout from "@/components/common/Layout";
import { useUser } from "@/contexts/UserContext";
import { useProjects } from "@/hooks/businessCentral";
import {
  useGetAllQaStatusItemsForProject,
  useUpdateQaStatus
} from "@/hooks/forecasting";
import { ISelectOption } from "@/types";
import { IQaStatusItem } from "@/types/forecasting";
import storage from "@/utils/storage";

const { Text } = Typography;

const ApprovalsPage: React.FC = () => {
  const [selectedProject, setSelectedProject] =
    React.useState<ISelectOption | null>(null);
  const [updatingStages, setUpdatingStages] = React.useState<Set<string>>(
    new Set()
  );

  const user = useUser();
  const updateQaStatus = useUpdateQaStatus();

  const { isLoading: projectsLoading, data: projectsData } = useProjects();
  const { isLoading: qaItemsLoading, data: qaItemsData } =
    useGetAllQaStatusItemsForProject(selectedProject?.opportunityNo);

  React.useEffect(() => {
    if (projectsData && projectsData.value?.length > 0) {
      const savedProjectName = storage.getProject()?.split("~~")?.[1];

      if (savedProjectName) {
        const target = projectsData.value.find(
          (item) =>
            user?.regionCode === item.jobPostingGroup &&
            item.description === savedProjectName
        );
        if (target) {
          setSelectedProject({
            key: target.no,
            value: target.description,
            label: `${target.no} - ${target.description}`,
            no: target.no,
            opportunityNo: target.opportunityNo,
            description: target.description
          });
        }
      }
    }
  }, [user, projectsData]);

  const handleQAStatusUpdate = async (
    record: IQaStatusItem,
    newStatus: "QA Review In Progress" | "QA Completed"
  ) => {
    const stageKey = `${record.ProjectName}-${record.JobType}-${record.Stage}`;
    setUpdatingStages((prev) => new Set(prev).add(stageKey));

    try {
      await updateQaStatus.mutateAsync({
        project: record.ProjectId,
        date: dayjs().format("YYYY-MM"),
        opportunityNo: selectedProject?.opportunityNo,
        payload: {
          jobType: record.JobType,
          plot: record.PlotOrRun,
          stage: record.Stage,
          qaStatus: newStatus,
          updatedBy: user?.email || ""
        }
      });

      toast.success(`QA Status updated to "${newStatus}" successfully`);
    } catch (error) {
      toast.error("Failed to update QA status");
    } finally {
      setUpdatingStages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(stageKey);
        return newSet;
      });
    }
  };

  const getQAStatusTag = (status: string | null) => {
    if (!status) return null;

    const statusConfig: Record<
      string,
      { color: string; icon: React.ReactNode }
    > = {
      "Pending QA": { color: "orange", icon: <ClockCircleOutlined /> },
      "QA In Progress": { color: "blue", icon: <SyncOutlined /> },
      "QA Review In Progress": { color: "purple", icon: <SyncOutlined /> },
      "QA Completed": { color: "green", icon: <CheckCircleOutlined /> }
    };

    const config = statusConfig[status] || { color: "default", icon: null };

    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Project",
      dataIndex: "ProjectId",
      key: "ProjectId",
      width: 150,
      fixed: "left" as const
    },
    {
      title: "Work Type",
      dataIndex: "JobType",
      key: "JobType",
      width: 150,
      render: (workType: string) => (
        <Tag color={workType === "Groundworks" ? "blue" : "green"}>
          {workType}
        </Tag>
      )
    },
    {
      title: "Plot/Run",
      dataIndex: "PlotOrRun",
      key: "PlotOrRun",
      width: 80,
      render: (val: string) => <p className="text-center">{val}</p>
    },
    {
      title: "Stage",
      dataIndex: "Stage",
      key: "Stage",
      width: 200
    },
    {
      title: "Current QA Status",
      dataIndex: "QAStatus",
      key: "QAStatus",
      width: 180,
      render: (status: string | null) => getQAStatusTag(status)
    },
    {
      title: "Update QA Status",
      key: "action",
      width: 250,
      render: (_: unknown, record: IQaStatusItem) => {
        const stageKey = `${record.ProjectName}-${record.JobType}-${record.Stage}`;
        const isUpdating = updatingStages.has(stageKey);
        const isNotQaReviewInProgress =
          record.QAStatus !== "QA Review In Progress";

        return (
          <Space>
            {["QA In Progress"].includes(record.QAStatus || "") && (
              <Button
                type="primary"
                size="small"
                loading={isUpdating}
                onClick={() =>
                  handleQAStatusUpdate(record, "QA Review In Progress")
                }
              >
                Review In Progress
              </Button>
            )}
            {isNotQaReviewInProgress ? (
              <Tooltip title="Set status to 'QA Review In Progress' to complete">
                <Button size="small" type="default" disabled={true}>
                  Complete
                </Button>
              </Tooltip>
            ) : (
              <Button
                size="small"
                type="primary"
                loading={isUpdating}
                onClick={() => handleQAStatusUpdate(record, "QA Completed")}
                className="!bg-green-600"
              >
                Complete
              </Button>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <Layout>
      <PageHeader
        title="QA Approvals"
        description={
          <>
            Manage QA status for stages. Only stages with &quot;QA In
            Progress&quot; or &quot;QA Review In Progress&quot; status can be
            updated.
          </>
        }
      />

      <div className="mb-6 p-4 bg-white rounded-lg">
        <Select
          size="large"
          placeholder="Search by project name..."
          showSearch
          allowClear
          value={selectedProject}
          onChange={(_, obj) => {
            if (obj && typeof obj === "object" && !Array.isArray(obj)) {
              storage.setProject(`${obj.no}~~${obj.description}`);
              setSelectedProject(obj);
            } else {
              storage.setProject("");
              setSelectedProject(null);
            }
          }}
          options={projectsData?.value?.reduce((acc: ISelectOption[], cur) => {
            if (
              cur?.jobPostingGroup &&
              cur.jobPostingGroup === user?.regionCode
            ) {
              acc.push({
                key: cur.no,
                value: cur.description,
                label: `${cur.no} - ${cur.description}`,
                no: cur.no,
                opportunityNo: cur.opportunityNo,
                description: cur.description
              });
            }
            return acc;
          }, [])}
          optionFilterProp="label"
          loading={projectsLoading}
          className="min-w-[400px]"
        />
      </div>

      {!selectedProject && (
        <div className="p-4 bg-white rounded-lg">
          <div className="text-center py-12">
            <ProjectOutlined className="text-6xl text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Select a project to get started
            </h3>
            <p className="text-gray-500">
              Choose a project from the dropdown above to view QA approvals.
            </p>
          </div>
        </div>
      )}

      {selectedProject && qaItemsLoading && <Card loading={true} />}

      {selectedProject &&
        !qaItemsLoading &&
        qaItemsData?.QAItems &&
        qaItemsData.QAItems.length > 0 && (
          <Table
            columns={columns}
            dataSource={qaItemsData.QAItems}
            rowKey={(record) =>
              `${record.ProjectName}-${record.JobType}-${record.PlotOrRun}-${record.Stage}`
            }
            pagination={false}
            scroll={{ x: 1200 }}
            size="small"
          />
        )}

      {selectedProject &&
        !qaItemsLoading &&
        (!qaItemsData?.QAItems || qaItemsData.QAItems.length === 0) && (
          <Card>
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Text type="secondary">
                No stages requiring QA approval for this project.
              </Text>
            </div>
          </Card>
        )}
    </Layout>
  );
};

export default ApprovalsPage;
